import type { AbortSignalPolyfill } from 'abort-signal-polyfill';
import { ReadonlySignal } from '@funtech-int/rival';
import { updatePreferredWatchingLanguageMutation } from '@funtech-int/kp-graphql-sdk/yango-movies-www';

import { GraphqlClient } from '@/shared/lib/graphqlClient';
import { InMemoryCache } from '@/shared/lib/inMemoryCache';
import { createSingleFlight } from '@/shared/lib/singleFlight';

// TODO: update ts https://st.yandex-team.ru/SAFTWEB-1093
declare const AbortSignal: AbortSignalPolyfill;

interface UserSettings {
    preferredLanguage: ReadonlySignal<string | null>;
    updatePreferredLanguage(code: string): Promise<string | null>;
}

interface UserSettingsParams {
    graphqlClient: GraphqlClient;
    inMemoryCache: InMemoryCache;
    abortSignal: AbortSignal;
}

type UserSettingsType = 'preferredLanguage';

export function getUserSettingsKey(type: UserSettingsType) {
    return `userSettings:${type}`;
}

export function createUserSettings({
    graphqlClient,
    inMemoryCache,
    abortSignal,
}: UserSettingsParams): UserSettings {
    const preferredLanguageKey = getUserSettingsKey('preferredLanguage');
    const preferredLanguage = inMemoryCache.getSignal<string | null>(preferredLanguageKey, null);

    const preferredLanguageMutation = createSingleFlight(
        (internalAbortSignal: AbortSignal, code: string) =>
            inMemoryCache.update(preferredLanguageKey, code, async (ctx) => {
                ctx.stage();

                try {
                    const result = await graphqlClient.fetch(
                        {
                            operation: updatePreferredWatchingLanguageMutation,
                            variables: { code },
                        },
                        { abortSignal: AbortSignal.any([abortSignal, internalAbortSignal]) }
                    );

                    ctx.commit();

                    return result.data?.data?.preferredWatchingLanguage ?? null;
                } catch (err) {
                    ctx.rollback();

                    throw err;
                }
            })
    );

    return {
        preferredLanguage,
        updatePreferredLanguage(code: string) {
            return preferredLanguageMutation.run(code);
        },
    };
}
